/**
 * ABOUTME: Utility functions for SmartQuote component
 * ABOUTME: Handles protocol generation, form validation, and platform detection
 */
import { Tradesperson } from '@/lib/types';
import { debug } from '@/lib/utils';
import { generateMessage, SmartQuoteTemplateData, hasPlaceholders } from '@/lib/messaging/templates';

// Form validation for contact info (simplified - only requires name)
export function validateContactForm(
  name: string
): { isValid: boolean; errorMessage: string | null } {
  if (!name.trim()) {
    return { isValid: false, errorMessage: 'Please enter your name' };
  }

  return { isValid: true, errorMessage: null };
}

// Generate message template with user details
export function generateMessageTemplate(
  text: string,
  postcode: string | undefined,
  name: string,
  jobType?: string,
  email?: string,
  phone?: string,
  isRepeatContact?: boolean
): string {
  // Validate required fields before attempting template generation
  const missingFields: string[] = [];
  if (!text || text.trim() === '') missingFields.push('job details');
  if (!postcode || postcode.trim() === '') missingFields.push('postcode');
  if (!name || name.trim() === '') missingFields.push('name');
  if (!jobType || jobType.trim() === '') missingFields.push('job type');

  // If we have all required data, use the new template system
  if (missingFields.length === 0) {
    try {
      const templateData: SmartQuoteTemplateData = {
        userName: name,
        postcode: postcode,
        jobType: jobType, // Pass as-is, let template handle formatting
        jobDetails: text,
        isFromSavedList: isRepeatContact
      };

      const { body } = generateMessage('smartQuote', templateData);
      return body;
    } catch (error) {
      debug.error('Error generating message with new template system:', error);
      // Continue to fallback method
    }
  } else {
    debug.warn('Missing required fields for template generation:', missingFields);
  }

  // Fallback template with user-friendly guidance
  const postcodeUpper = postcode?.toUpperCase() || '[Please enter your postcode]';
  const jobTypeFormatted = jobType ? jobType.toLowerCase() : '[Please select a trade type]';
  const jobDetails = text || '[Please describe what you need help with]';

  return `Hi there,

I'm looking for ${jobType ? (/^[aeiou]/i.test(jobType) ? `an ${jobTypeFormatted}` : `a ${jobTypeFormatted}`) : jobTypeFormatted} in ${postcodeUpper} and my neighbours recommended you.

${jobDetails}

Are you available to discuss this? When would suit you?

Thanks,`;
}

// Update template with actual user name
export function updateMessageWithName(message: string, name: string): string {
  return message.replace(/Thanks,\n\[Your name\]|Thanks,\n.*$/, `Thanks,\n${name}`);
}

// Generate mailto URL for email sending
export function generateMailtoUrl(
  selectedTradespeople: Tradesperson[],
  selectedRecipients: Record<string, boolean>,
  subject: string = 'Quote Request',
  message: string
): string {
  const selectedEmails = selectedTradespeople
    .filter(trade => trade.email && selectedRecipients[trade.id])
    .map(trade => trade.email);

  if (selectedEmails.length === 0) {
    return '';
  }

  const body = encodeURIComponent(message);

  // Use BCC for privacy between recipients
  return `mailto:?bcc=${selectedEmails.join(',')}&subject=${encodeURIComponent(subject)}&body=${body}`;
}

// Generate SMS link for text messaging
export function generateSmsLink(phoneNumber: string, message: string): string {
  if (!phoneNumber) return '';

  const body = encodeURIComponent(message);

  // Use different formats based on platform
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    return `sms:${phoneNumber}&body=${body}`;
  } else {
    return `sms:${phoneNumber}?body=${body}`;
  }
}

// Detect mobile platform
export function isMobilePlatform(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

// Detect Safari browser
export function isSafari(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

// Detect mobile Safari specifically
export function isMobileSafari(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as Window & { MSStream?: unknown }).MSStream &&
    /Safari/.test(navigator.userAgent);
}

// Open URL via direct DOM approach
export function openUrl(url: string): boolean {
  try {
    // Create and trigger a link element directly
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener noreferrer';

    // Add to DOM, click, then remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  } catch (error) {
    debug.error('Error opening URL:', error);
    return false;
  }
}

// Export the hasPlaceholders function for UI use
export { hasPlaceholders };
